import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Calendar, Building2, Globe, Presentation, Users, Smartphone, Sparkles, BarChart3, CheckSquare } from "lucide-react";
import "./StartFreeTrial.css";
import usePost from "../../../../Hooks/usePost";
import logo from "../../../../assets/images/brands/kago-events-logo-white.png";
import EventScene from "../../../../assets/Group 1000002088.png";

const africanCountries = [
  "Algeria", "Angola", "Benin", "Botswana", "Burkina Faso", "Burundi", "Cameroon",
  "Cape Verde", "Central African Republic", "Chad", "Comoros", "Congo", "Côte d'Ivoire",
  "Democratic Republic of the Congo", "Djibouti", "Egypt", "Equatorial Guinea", "Eritrea",
  "Eswatini", "Ethiopia", "Gabon", "Gambia", "Ghana", "Guinea", "Guinea-Bissau", "Kenya",
  "Lesotho", "Liberia", "Libya", "Madagascar", "Malawi", "Mali", "Mauritania", "Mauritius",
  "Morocco", "Mozambique", "Namibia", "Niger", "Nigeria", "Rwanda", "São Tomé and Príncipe",
  "Senegal", "Seychelles", "Sierra Leone", "Somalia", "South Africa", "South Sudan", "Sudan",
  "Tanzania", "Togo", "Tunisia", "Uganda", "Zambia", "Zimbabwe"
];

const eventTypes = ["Conference", "Exhibition", "Webinar", "Expo"];
const eventFormats = ["Hybrid", "Virtual", "In Person"];

interface RegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirm: string;
  organizationName: string;
  country: string;
  eventType: string;
  eventFormat: string;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  passwordConfirm?: string;
  organizationName?: string;
  country?: string;
  eventType?: string;
  eventFormat?: string;
}

const StartFreeTrial = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const { execute, pending, data, status } = usePost();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RegistrationData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordConfirm: "",
    organizationName: "",
    country: "",
    eventType: "",
    eventFormat: "",
  });

  const handleInputChange = (field: keyof RegistrationData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = "First name is too long";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = "Last name is too long";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Invalid email address";
    } else if (formData.email.length > 255) {
      newErrors.email = "Email is too long";
    }

    if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = "Password must contain at least one uppercase letter";
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = "Password must contain at least one lowercase letter";
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = "Password must contain at least one number";
    }

    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = "Please confirm your password";
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = "Passwords don't match";
    }

    if (!formData.organizationName.trim()) {
      newErrors.organizationName = "Organization name is required";
    } else if (formData.organizationName.length > 200) {
      newErrors.organizationName = "Organization name is too long";
    }

    if (!formData.country) {
      newErrors.country = "Please select a country";
    }

    // if (!formData.eventType) {
    //   newErrors.eventType = "Please select an event type";
    // }

    // if (!formData.eventFormat) {
    //   newErrors.eventFormat = "Please select an event format";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    
    e.preventDefault();

    if (!validateForm()) {
      console.log("not validated");
      return;
    }
    console.log("triggered 1");

    setIsSubmitting(true);
    console.log("triggered 2");

    try {
      // await new Promise((resolve) => setTimeout(resolve, 1000));
      // alert("Registration Successful! Welcome to Kago Events.");

      const Method = "POST",
      url = `auth/event-organizer`,
      isJSON = true;
      const jsonBody = JSON.stringify(formData);
      execute(url, jsonBody, Method, "Registered successfully", null, isJSON, true);

      // console.log("Form data:", jsonBody);

    } catch (error) {
      alert("Registration Failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
      if (status === "success") {
        navigate("/check-email", { replace: true });
      }
    }, [status]);

  const passwordValidations = [
    { label: "At least 8 characters", valid: formData.password.length >= 8 },
    { label: "Contains uppercase letter", valid: /[A-Z]/.test(formData.password) },
    { label: "Contains lowercase letter", valid: /[a-z]/.test(formData.password) },
    { label: "Contains number", valid: /[0-9]/.test(formData.password) },
  ];

  return (
    <div className="start-free-trial-container">
      <div className="sft-left-section">
        <div className="sft-logo">
          <img src={logo} alt="Kago Events" className="sft-logo-image" />
        </div>
        
        <div className="sft-main-heading">
          <h1>Get our Enterprise Access <br /> for unlimited features</h1>
        </div>

        <div className="sft-center-image-container">
          <img src={EventScene} alt="Event Scene" className="sft-center-image" />
          
          {/* <div className="sft-feature-box sft-feature-box-top-left">
            <Smartphone className="sft-feature-box-icon" />
            <span>Event Mobile App</span>
          </div>
          
          <div className="sft-feature-box sft-feature-box-top-right">
            <Sparkles className="sft-feature-box-icon" />
            <span>AI Features</span>
          </div>
          
          <div className="sft-feature-box sft-feature-box-bottom-left">
            <BarChart3 className="sft-feature-box-icon" />
            <span>Analytics & Reporting</span>
          </div>
          
          <div className="sft-feature-box sft-feature-box-bottom-right">
            <CheckSquare className="sft-feature-box-icon" />
            <span>Custom Branding</span>
          </div> */}
        </div>

        <div className="sft-footer-text">
          <p>By TNG Solutions | Developed in South Africa</p>
        </div>
      </div>

      <div className="sft-right-section">
        <div className="sft-form-wrapper">
          <div className="sft-mobile-logo">
            <Calendar />
            <h1>Kago Events</h1>
          </div>

          <div className="sft-header">
            <h2>Start your free account</h2>
            <p>
              Already have an account?{" "}
              <a href="/login">Log in now</a>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="sft-form">
            <div className="sft-section">
              <h3 className="sft-section-title">
                <Building2 />
                Personal Information
              </h3>

              <div className="sft-form-row">
                <div className="sft-field">
                  <label htmlFor="firstName" className="sft-label">First Name *</label>
                  <input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    className={`sft-input ${errors.firstName ? "error" : ""}`}
                    disabled={isSubmitting}
                  />
                  {errors.firstName && (
                    <p className="sft-error">{errors.firstName}</p>
                  )}
                </div>
                <div className="sft-field">
                  <label htmlFor="lastName" className="sft-label">Last Name *</label>
                  <input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    className={`sft-input ${errors.lastName ? "error" : ""}`}
                    disabled={isSubmitting}
                  />
                  {errors.lastName && (
                    <p className="sft-error">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div className="sft-field">
                <label htmlFor="email" className="sft-label">Email Address *</label>
                <p className="sft-help-text">We recommend using your work email</p>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={`sft-input ${errors.email ? "error" : ""}`}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="sft-error">{errors.email}</p>
                )}
              </div>

              <div className="sft-field">
                <label htmlFor="password" className="sft-label">Password *</label>
                <div className="sft-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className={`sft-input sft-input-with-icon ${errors.password ? "error" : ""}`}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="sft-toggle-password"
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                <div className="sft-password-validations">
                  {passwordValidations.map((validation, index) => (
                    <div key={index} className="sft-validation-item">
                      <div className={`sft-validation-dot ${validation.valid ? "valid" : ""}`} />
                      <span className={`sft-validation-text ${validation.valid ? "valid" : ""}`}>
                        {validation.label}
                      </span>
                    </div>
                  ))}
                </div>
                {errors.password && (
                  <p className="sft-error">{errors.password}</p>
                )}
              </div>

              <div className="sft-field">
                <label htmlFor="passwordConfirm" className="sft-label">Confirm Password *</label>
                <div className="sft-input-wrapper">
                  <input
                    id="passwordConfirm"
                    type={showPasswordConfirm ? "text" : "password"}
                    value={formData.passwordConfirm}
                    onChange={(e) => handleInputChange("passwordConfirm", e.target.value)}
                    className={`sft-input sft-input-with-icon ${errors.passwordConfirm ? "error" : ""}`}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="sft-toggle-password"
                  >
                    {showPasswordConfirm ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                {errors.passwordConfirm && (
                  <p className="sft-error">{errors.passwordConfirm}</p>
                )}
              </div>
            </div>

            <div className="sft-section sft-divider">
              <h3 className="sft-section-title">
                <Presentation />
                Organization & Event Details
              </h3>

              <div className="sft-field">
                <label htmlFor="organizationName" className="sft-label">Organization Name *</label>
                <input
                  id="organizationName"
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => handleInputChange("organizationName", e.target.value)}
                  className={`sft-input ${errors.organizationName ? "error" : ""}`}
                  disabled={isSubmitting}
                />
                {errors.organizationName && (
                  <p className="sft-error">{errors.organizationName}</p>
                )}
              </div>

              <div className="sft-field">
                <label htmlFor="country" className="sft-label">Country *</label>
                <select
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  className={`sft-select ${errors.country ? "error" : ""}`}
                  disabled={isSubmitting}
                >
                  <option value="">Select your country</option>
                  {africanCountries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="sft-error">{errors.country}</p>
                )}
              </div>

              {/* <div className="sft-form-row">
                <div className="sft-field">
                  <label htmlFor="eventType" className="sft-label">Event Type *</label>
                  <select
                    id="eventType"
                    value={formData.eventType}
                    onChange={(e) => handleInputChange("eventType", e.target.value)}
                    className={`sft-select ${errors.eventType ? "error" : ""}`}
                    disabled={isSubmitting}
                  >
                    <option value="">Select type</option>
                    {eventTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.eventType && (
                    <p className="sft-error">{errors.eventType}</p>
                  )}
                </div>

                <div className="sft-field">
                  <label htmlFor="eventFormat" className="sft-label">Event Format *</label>
                  <select
                    id="eventFormat"
                    value={formData.eventFormat}
                    onChange={(e) => handleInputChange("eventFormat", e.target.value)}
                    className={`sft-select ${errors.eventFormat ? "error" : ""}`}
                    disabled={isSubmitting}
                  >
                    <option value="">Select format</option>
                    {eventFormats.map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                  {errors.eventFormat && (
                    <p className="sft-error">{errors.eventFormat}</p>
                  )}
                </div>
              </div> */}
            </div>

            <button
              type="submit"
              className="sft-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating Account..." : "Start For Free"}
            </button>

            <p className="sft-footer">
              By continuing, you agree to the{" "}
              <a href="/privacy">Privacy Policy</a>{" "}
              and{" "}
              <a href="/terms">Terms of Service</a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StartFreeTrial;


// --------------------------------- OLD CODE - FOR REFERENCE ONLY ---------------------------------

// import { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { Button } from "../../../../components/ui/button.tsx";
// import { Input } from "../../../../components/ui/input.tsx";
// import { Label } from "../../../../components/ui/label.tsx";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select.tsx";
// import { useToast } from "../../../../Hooks/use-toast.ts";
// import { Eye, EyeOff, Calendar, Building2, Globe, Presentation, Users } from "lucide-react";
// import { z } from "zod";
// import abstractBg from "../../../../assets/abstract-bg.png";

// const africanCountries = [
//   "Algeria", "Angola", "Benin", "Botswana", "Burkina Faso", "Burundi", "Cameroon",
//   "Cape Verde", "Central African Republic", "Chad", "Comoros", "Congo", "Côte d'Ivoire",
//   "Democratic Republic of the Congo", "Djibouti", "Egypt", "Equatorial Guinea", "Eritrea",
//   "Eswatini", "Ethiopia", "Gabon", "Gambia", "Ghana", "Guinea", "Guinea-Bissau", "Kenya",
//   "Lesotho", "Liberia", "Libya", "Madagascar", "Malawi", "Mali", "Mauritania", "Mauritius",
//   "Morocco", "Mozambique", "Namibia", "Niger", "Nigeria", "Rwanda", "São Tomé and Príncipe",
//   "Senegal", "Seychelles", "Sierra Leone", "Somalia", "South Africa", "South Sudan", "Sudan",
//   "Tanzania", "Togo", "Tunisia", "Uganda", "Zambia", "Zimbabwe"
// ];

// const eventTypes = ["Conference", "Exhibition", "Webinar", "Expo"];
// const eventFormats = ["Hybrid", "Virtual", "In Person"];

// const registrationSchema = z.object({
//   firstName: z.string().trim().min(1, "First name is required").max(100),
//   lastName: z.string().trim().min(1, "Last name is required").max(100),
//   email: z.string().trim().email("Invalid email address").max(255),
//   password: z.string().min(8, "Password must be at least 8 characters")
//     .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
//     .regex(/[a-z]/, "Password must contain at least one lowercase letter")
//     .regex(/[0-9]/, "Password must contain at least one number"),
//   passwordConfirm: z.string().min(1, "Please confirm your password"),
//   organizationName: z.string().trim().min(1, "Organization name is required").max(200),
//   country: z.string().min(1, "Please select a country"),
//   eventType: z.string().min(1, "Please select an event type"),
//   eventFormat: z.string().min(1, "Please select an event format"),
// }).refine((data) => data.password === data.passwordConfirm, {
//   message: "Passwords don't match",
//   path: ["passwordConfirm"],
// });

// type RegistrationData = z.infer<typeof registrationSchema>;

// const RegistrationForm = () => {
//   const navigate = useNavigate();
//   const { toast } = useToast();
//   const [showPassword, setShowPassword] = useState(false);
//   const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [errors, setErrors] = useState<Partial<Record<keyof RegistrationData, string>>>({});
  
//   const [formData, setFormData] = useState<RegistrationData>({
//     firstName: "",
//     lastName: "",
//     email: "",
//     password: "",
//     passwordConfirm: "",
//     organizationName: "",
//     country: "",
//     eventType: "",
//     eventFormat: "",
//   });

//   const handleInputChange = (field: keyof RegistrationData, value: string) => {
//     setFormData((prev) => ({ ...prev, [field]: value }));
//     // Clear error for this field when user starts typing
//     if (errors[field]) {
//       setErrors((prev) => ({ ...prev, [field]: undefined }));
//     }
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     setErrors({});
    
//     try {
//       // Validate form data
//       const validatedData = registrationSchema.parse(formData);
      
//       setIsSubmitting(true);
      
//       // Simulate API call (replace with actual API integration)
//       const formDataToSend = new FormData();
//       Object.entries(validatedData).forEach(([key, value]) => {
//         formDataToSend.append(key, value);
//       });
      
//       // TODO: Replace with actual API call
//       // const response = await fetch('auth/attendee', {
//       //   method: 'POST',
//       //   body: formDataToSend,
//       // });
      
//       // Simulate success
//       await new Promise((resolve) => setTimeout(resolve, 1000));
      
//       toast({
//         title: "Registration Successful!",
//         description: "Welcome to Kago Events. Redirecting to login...",
//       });
      
//       setTimeout(() => {
//         navigate("/login");
//       }, 1500);
      
//     } catch (error) {
//       if (error instanceof z.ZodError) {
//         const newErrors: Partial<Record<keyof RegistrationData, string>> = {};
//         error.errors.forEach((err) => {
//           if (err.path[0]) {
//             newErrors[err.path[0] as keyof RegistrationData] = err.message;
//           }
//         });
//         setErrors(newErrors);
        
//         toast({
//           title: "Validation Error",
//           description: "Please check the form for errors.",
//           variant: "destructive",
//         });
//       } else {
//         toast({
//           title: "Registration Failed",
//           description: "An error occurred. Please try again.",
//           variant: "destructive",
//         });
//       }
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const passwordValidations = [
//     { label: "At least 8 characters", valid: formData.password.length >= 8 },
//     { label: "Contains uppercase letter", valid: /[A-Z]/.test(formData.password) },
//     { label: "Contains lowercase letter", valid: /[a-z]/.test(formData.password) },
//     { label: "Contains number", valid: /[0-9]/.test(formData.password) },
//   ];

//   return (
//     <div className="min-h-screen flex">
//       {/* Left side - Abstract graphics */}
//       <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-kago-black via-kago-black to-gray-900 overflow-hidden">
//         <div className="absolute inset-0 opacity-30">
//           <img 
//             src={abstractBg} 
//             alt="" 
//             className="w-full h-full object-cover"
//           />
//         </div>
//         <div className="relative z-10 flex flex-col justify-center items-start p-16 text-white">
//           <div className="mb-8">
//             <Calendar className="w-16 h-16 text-primary mb-4" />
//             <h1 className="text-5xl font-bold mb-4">Kago Events</h1>
//             <p className="text-2xl text-gray-300">Create Memorable Experiences</p>
//           </div>
//           <div className="space-y-6 mt-12">
//             <div className="flex items-start gap-4">
//               <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
//                 <Presentation className="w-6 h-6 text-primary" />
//               </div>
//               <div>
//                 <h3 className="font-semibold text-lg mb-1">Professional Events</h3>
//                 <p className="text-gray-400">Host conferences, webinars, and exhibitions with ease</p>
//               </div>
//             </div>
//             <div className="flex items-start gap-4">
//               <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
//                 <Users className="w-6 h-6 text-primary" />
//               </div>
//               <div>
//                 <h3 className="font-semibold text-lg mb-1">Engage Your Audience</h3>
//                 <p className="text-gray-400">Connect with attendees across multiple formats</p>
//               </div>
//             </div>
//             <div className="flex items-start gap-4">
//               <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
//                 <Globe className="w-6 h-6 text-primary" />
//               </div>
//               <div>
//                 <h3 className="font-semibold text-lg mb-1">Global Reach</h3>
//                 <p className="text-gray-400">Hybrid and virtual options for worldwide participation</p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Right side - Form */}
//       <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-background">
//         <div className="w-full max-w-md">
//           {/* Mobile logo */}
//           <div className="lg:hidden mb-8 text-center">
//             <Calendar className="w-12 h-12 text-primary mx-auto mb-3" />
//             <h1 className="text-3xl font-bold text-foreground">Kago Events</h1>
//           </div>

//           <div className="mb-8">
//             <h2 className="text-3xl font-bold text-foreground mb-2">Start your free trial</h2>
//             <p className="text-muted-foreground">
//               Already have an account?{" "}
//               <a href="/login" className="text-primary hover:underline font-medium">
//                 Log in now
//               </a>
//             </p>
//           </div>

//           <form onSubmit={handleSubmit} className="space-y-5">
//             {/* Personal Information Section */}
//             <div className="space-y-4">
//               <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
//                 <Building2 className="w-4 h-4 text-primary" />
//                 Personal Information
//               </h3>
              
//               <div className="grid grid-cols-2 gap-4">
//                 <div className="space-y-2">
//                   <Label htmlFor="firstName">First Name *</Label>
//                   <Input
//                     id="firstName"
//                     type="text"
//                     value={formData.firstName}
//                     onChange={(e) => handleInputChange("firstName", e.target.value)}
//                     className={errors.firstName ? "border-destructive" : ""}
//                     disabled={isSubmitting}
//                   />
//                   {errors.firstName && (
//                     <p className="text-xs text-destructive">{errors.firstName}</p>
//                   )}
//                 </div>
//                 <div className="space-y-2">
//                   <Label htmlFor="lastName">Last Name *</Label>
//                   <Input
//                     id="lastName"
//                     type="text"
//                     value={formData.lastName}
//                     onChange={(e) => handleInputChange("lastName", e.target.value)}
//                     className={errors.lastName ? "border-destructive" : ""}
//                     disabled={isSubmitting}
//                   />
//                   {errors.lastName && (
//                     <p className="text-xs text-destructive">{errors.lastName}</p>
//                   )}
//                 </div>
//               </div>

//               <div className="space-y-2">
//                 <Label htmlFor="email">Email Address *</Label>
//                 <p className="text-xs text-muted-foreground">
//                   We recommend using your work email
//                 </p>
//                 <Input
//                   id="email"
//                   type="email"
//                   value={formData.email}
//                   onChange={(e) => handleInputChange("email", e.target.value)}
//                   className={errors.email ? "border-destructive" : ""}
//                   disabled={isSubmitting}
//                 />
//                 {errors.email && (
//                   <p className="text-xs text-destructive">{errors.email}</p>
//                 )}
//               </div>

//               <div className="space-y-2">
//                 <Label htmlFor="password">Password *</Label>
//                 <div className="relative">
//                   <Input
//                     id="password"
//                     type={showPassword ? "text" : "password"}
//                     value={formData.password}
//                     onChange={(e) => handleInputChange("password", e.target.value)}
//                     className={errors.password ? "border-destructive pr-10" : "pr-10"}
//                     disabled={isSubmitting}
//                   />
//                   <button
//                     type="button"
//                     onClick={() => setShowPassword(!showPassword)}
//                     className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
//                   >
//                     {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
//                   </button>
//                 </div>
//                 <div className="space-y-1 mt-2">
//                   {passwordValidations.map((validation, index) => (
//                     <div key={index} className="flex items-center gap-2 text-xs">
//                       <div
//                         className={`w-1 h-1 rounded-full ${
//                           validation.valid ? "bg-green-500" : "bg-gray-300"
//                         }`}
//                       />
//                       <span className={validation.valid ? "text-green-600" : "text-muted-foreground"}>
//                         {validation.label}
//                       </span>
//                     </div>
//                   ))}
//                 </div>
//                 {errors.password && (
//                   <p className="text-xs text-destructive">{errors.password}</p>
//                 )}
//               </div>

//               <div className="space-y-2">
//                 <Label htmlFor="passwordConfirm">Confirm Password *</Label>
//                 <div className="relative">
//                   <Input
//                     id="passwordConfirm"
//                     type={showPasswordConfirm ? "text" : "password"}
//                     value={formData.passwordConfirm}
//                     onChange={(e) => handleInputChange("passwordConfirm", e.target.value)}
//                     className={errors.passwordConfirm ? "border-destructive pr-10" : "pr-10"}
//                     disabled={isSubmitting}
//                   />
//                   <button
//                     type="button"
//                     onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
//                     className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
//                   >
//                     {showPasswordConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
//                   </button>
//                 </div>
//                 {errors.passwordConfirm && (
//                   <p className="text-xs text-destructive">{errors.passwordConfirm}</p>
//                 )}
//               </div>
//             </div>

//             {/* Organization & Event Section */}
//             <div className="space-y-4 pt-4 border-t">
//               <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2">
//                 <Presentation className="w-4 h-4 text-primary" />
//                 Organization & Event Details
//               </h3>

//               <div className="space-y-2">
//                 <Label htmlFor="organizationName">Organization Name *</Label>
//                 <Input
//                   id="organizationName"
//                   type="text"
//                   value={formData.organizationName}
//                   onChange={(e) => handleInputChange("organizationName", e.target.value)}
//                   className={errors.organizationName ? "border-destructive" : ""}
//                   disabled={isSubmitting}
//                 />
//                 {errors.organizationName && (
//                   <p className="text-xs text-destructive">{errors.organizationName}</p>
//                 )}
//               </div>

//               <div className="space-y-2">
//                 <Label htmlFor="country">Country *</Label>
//                 <Select
//                   value={formData.country}
//                   onValueChange={(value) => handleInputChange("country", value)}
//                   disabled={isSubmitting}
//                 >
//                   <SelectTrigger className={errors.country ? "border-destructive" : ""}>
//                     <SelectValue placeholder="Select your country" />
//                   </SelectTrigger>
//                   <SelectContent className="bg-popover">
//                     {africanCountries.map((country) => (
//                       <SelectItem key={country} value={country}>
//                         {country}
//                       </SelectItem>
//                     ))}
//                   </SelectContent>
//                 </Select>
//                 {errors.country && (
//                   <p className="text-xs text-destructive">{errors.country}</p>
//                 )}
//               </div>

//               <div className="grid grid-cols-2 gap-4">
//                 <div className="space-y-2">
//                   <Label htmlFor="eventType">Event Type *</Label>
//                   <Select
//                     value={formData.eventType}
//                     onValueChange={(value) => handleInputChange("eventType", value)}
//                     disabled={isSubmitting}
//                   >
//                     <SelectTrigger className={errors.eventType ? "border-destructive" : ""}>
//                       <SelectValue placeholder="Select type" />
//                     </SelectTrigger>
//                     <SelectContent className="bg-popover">
//                       {eventTypes.map((type) => (
//                         <SelectItem key={type} value={type}>
//                           {type}
//                         </SelectItem>
//                       ))}
//                     </SelectContent>
//                   </Select>
//                   {errors.eventType && (
//                     <p className="text-xs text-destructive">{errors.eventType}</p>
//                   )}
//                 </div>

//                 <div className="space-y-2">
//                   <Label htmlFor="eventFormat">Event Format *</Label>
//                   <Select
//                     value={formData.eventFormat}
//                     onValueChange={(value) => handleInputChange("eventFormat", value)}
//                     disabled={isSubmitting}
//                   >
//                     <SelectTrigger className={errors.eventFormat ? "border-destructive" : ""}>
//                       <SelectValue placeholder="Select format" />
//                     </SelectTrigger>
//                     <SelectContent className="bg-popover">
//                       {eventFormats.map((format) => (
//                         <SelectItem key={format} value={format}>
//                           {format}
//                         </SelectItem>
//                       ))}
//                     </SelectContent>
//                   </Select>
//                   {errors.eventFormat && (
//                     <p className="text-xs text-destructive">{errors.eventFormat}</p>
//                   )}
//                 </div>
//               </div>
//             </div>

//             <Button
//               type="submit"
//               className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 text-base"
//               disabled={isSubmitting}
//             >
//               {isSubmitting ? "Creating Account..." : "Start Free Trial"}
//             </Button>

//             <p className="text-xs text-center text-muted-foreground">
//               By continuing, you agree to the{" "}
//               <a href="/privacy" className="text-primary hover:underline">
//                 Privacy Policy
//               </a>{" "}
//               and{" "}
//               <a href="/terms" className="text-primary hover:underline">
//                 Terms of Service
//               </a>
//             </p>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default RegistrationForm;
